import { __assign } from "tslib";
import { isInPolygon } from './geoLogic';
import { isMultiLocationSite } from './multilocationLogic';
import moment from 'moment-timezone';
import _ from 'lodash';
import { isAvailableOn, iteratorFactory } from 'availability';
import { DEFAULT_ADDRESS, DEFAULT_ADDRESS_SHORT, EMPTY_ADDRESS, NO_ADDRESS, } from '../constants';
import { getAvailableDeliveryInfos, getDateOptions, isAvailabilityIteratorAvailable, } from './dateTimeLogic';
import { getNumberOfDaysAheadAvailableForOrders, isDeliveryAvailable, } from './restaurantLogic';
import { simpleMemoize } from './simpleMemoize';
export function isDeliveryPartnerArea(dispatchInfo) {
    return (dispatchInfo.deliveryProviderInfo !== undefined);
}
export function getValidatedDeliveryPartnerArea(restaurant, totalOrder, skipMinimumPriceCheck, dispatchTime, skipAvailabilityCheck) {
    var reasons = [];
    var deliverParnterAreaInfo = restaurant.deliveryInfos.find(function (info) {
        return isDeliveryPartnerArea(info) &&
            getAvailableDeliveryInfos([info], restaurant.timezone).length > 0;
    });
    if (deliverParnterAreaInfo) {
        reasons.push.apply(reasons, isAddressMatchingDispatchInfo(restaurant, EMPTY_ADDRESS, deliverParnterAreaInfo, moment(dispatchTime).tz(restaurant.timezone), totalOrder, {
            skipOutOfBoundsCheck: true,
            skipMinimumPriceCheck: skipMinimumPriceCheck,
            skipAvailabilityCheck: skipAvailabilityCheck,
        }));
    }
    return deliverParnterAreaInfo
        ? {
            info: deliverParnterAreaInfo,
            reasons: reasons,
        }
        : undefined;
}
export function isAddress(address) {
    var _a, _b;
    return (Boolean(address === null || address === void 0 ? void 0 : address.formatted) &&
        typeof ((_a = address === null || address === void 0 ? void 0 : address.latLng) === null || _a === void 0 ? void 0 : _a.lat) === 'number' &&
        typeof ((_b = address === null || address === void 0 ? void 0 : address.latLng) === null || _b === void 0 ? void 0 : _b.lng) === 'number');
}
export function validateAddress(_a) {
    var address = _a.address, restaurant = _a.restaurant, totalOrderPrice = _a.totalOrderPrice, dispatchTime = _a.dispatchTime, deliveryPartnerProps = _a.deliveryPartnerProps;
    if (!isAddress(address)) {
        return { type: 'invalid-address' };
    }
    else if (getIsPinpointError(address, restaurant)) {
        return { type: 'pinpoint-error' };
    }
    else {
        var deliveryAreaError = getIdealDeliveryArea({
            totalOrderPrice: totalOrderPrice,
            restaurant: restaurant,
            address: address,
            dispatchTime: dispatchTime,
            deliveryPartnerProps: deliveryPartnerProps,
        }).reason;
        return deliveryAreaError;
    }
}
export function getIsPinpointError(address, restaurant) {
    var approximateAddresses = restaurant.deliveriesInfo.approximateAddresses;
    return approximateAddresses === 'disallow' && !address.number;
}
export function getMinOrderPriceForIdealArea(_a) {
    var totalOrderPrice = _a.totalOrderPrice, dispatchTime = _a.dispatchTime, address = _a.address, restaurant = _a.restaurant, options = _a.options;
    // First, get all delivery areas matching the address lag/lng
    var inBounds = getAllInBounds({
        restaurant: restaurant,
        address: address,
        dispatchTime: dispatchTime,
        totalOrderPrice: totalOrderPrice,
        options: options,
    });
    // Now, get the best (=cheapest) available delivery area, if exists.
    var idealDeliveryArea = _(inBounds)
        .filter(function (v) { return v.reasons.length === 0; })
        .sortBy(function (v) { return v.info.charge || 0; })
        .first();
    return idealDeliveryArea ? idealDeliveryArea.info.minOrderPrice : undefined;
}
export function getIdealDeliveryArea(_a) {
    var _b;
    var totalOrderPrice = _a.totalOrderPrice, dispatchTime = _a.dispatchTime, address = _a.address, restaurant = _a.restaurant, options = _a.options, _c = _a.deliveryPartnerProps, deliveryPartnerProps = _c === void 0 ? {
        deliveryPartnerFee: undefined,
        shouldConsiderDeliveryPartner: false,
    } : _c;
    // First, get all delivery areas matching the address lag/lng
    var deliveryPartnerFee = deliveryPartnerProps.deliveryPartnerFee, shouldConsiderDeliveryPartner = deliveryPartnerProps.shouldConsiderDeliveryPartner;
    var inBounds = getAllInBounds({
        restaurant: restaurant,
        address: address,
        dispatchTime: dispatchTime,
        totalOrderPrice: totalOrderPrice,
        options: options,
    });
    // Now, get the best (=cheapest) available self delivery area, if exists.
    var idealDeliveryArea = _(inBounds)
        .filter(function (v) { return v.reasons.length === 0; })
        .sortBy(function (v) { return v.info.charge || 0; })
        .first();
    var isValid = Boolean(idealDeliveryArea);
    var reason = getMostImportantReason(inBounds);
    var deliveryPartnerArea = shouldConsiderDeliveryPartner
        ? getValidatedDeliveryPartnerArea(restaurant, totalOrderPrice, options === null || options === void 0 ? void 0 : options.skipMinimumPriceCheck, dispatchTime, options === null || options === void 0 ? void 0 : options.skipAvailabilityCheck)
        : undefined;
    if (!isValid &&
        (reason === null || reason === void 0 ? void 0 : reason.type) !== 'minimum-price' &&
        deliveryPartnerArea &&
        deliveryPartnerArea.reasons.length < 1) {
        return {
            isValid: true,
            dispatchInfo: __assign(__assign({}, deliveryPartnerArea.info), { charge: deliveryPartnerFee }),
            reason: undefined,
        };
    }
    if (!isValid &&
        deliveryPartnerArea &&
        deliveryPartnerArea.reasons.length > 0 &&
        (reason === null || reason === void 0 ? void 0 : reason.type) !== 'minimum-price' &&
        ((_b = getMostImportantReason([deliveryPartnerArea])) === null || _b === void 0 ? void 0 : _b.type) === 'minimum-price') {
        return {
            isValid: false,
            dispatchInfo: __assign(__assign({}, deliveryPartnerArea.info), { charge: deliveryPartnerFee }),
            reason: {
                type: 'minimum-price',
                minPrice: deliveryPartnerArea.reasons[0].minPrice,
            },
        };
    }
    return {
        isValid: isValid,
        dispatchInfo: idealDeliveryArea === null || idealDeliveryArea === void 0 ? void 0 : idealDeliveryArea.info,
        reason: !isValid ? reason : undefined,
    };
}
/**
 * @returns location that can deliver to the address or a reason why not
 */
export function validateAddressForLocations(locations, address, deliveryPartnerProps) {
    if (deliveryPartnerProps === void 0) { deliveryPartnerProps = {
        deliveryPartnerFee: undefined,
        shouldConsiderDeliveryPartner: false,
    }; }
    // 1. check if the address in valid
    // 2. check if the address contains address number when required by a location
    // 3. check if the locations can deliver to the address
    //    - filter locations with available delivery
    //    - filter locaions that can deliver to the address
    if (!isAddress(address)) {
        return { hasLocations: false, reason: { type: 'invalid-address' } };
    }
    else if (isAllLocationHavePinpointError()) {
        return { hasLocations: false, reason: { type: 'pinpoint-error' } };
    }
    else {
        var validLocations = locations
            .filter(function (location) { return isDeliveryAvailable(location); })
            .filter(function (location) {
            var deliveryArea = getIdealDeliveryArea({
                address: address,
                restaurant: location,
                dispatchTime: Date.now(),
                totalOrderPrice: 0,
                options: {
                    skipAvailabilityCheck: true,
                    skipMinimumPriceCheck: true,
                },
                deliveryPartnerProps: deliveryPartnerProps,
            });
            if (!deliveryArea.isValid) {
                return false;
            }
            return true;
        });
        // at this point after we filtered the locations that cannot deliver to the address
        // if we have no location, we are sure this is because out-of-bounds reason
        if (validLocations.length === 0) {
            return { hasLocations: false, reason: { type: 'out-of-bounds' } };
        }
        else {
            return {
                hasLocations: true,
                locations: validLocations,
            };
        }
    }
    function isAllLocationHavePinpointError() {
        return (locations.filter(function (location) { return !getIsPinpointError(address, location); })
            .length === 0);
    }
}
/**
 * @returns locations that have takeout enabled
 */
export function getLocationsWithTakeout(locations) {
    return locations.filter(function (location) {
        var pickupDispatchInfo = location.deliveryInfos.find(function (di) { return di.type === 'takeout'; });
        if (!pickupDispatchInfo || pickupDispatchInfo.inactive) {
            return false;
        }
        if (!hasAsapOrFutureAvailability(pickupDispatchInfo, location)) {
            return false;
        }
        return true;
    });
}
export function getLocationsWithDineIn(locations) {
    return locations.filter(function (location) {
        var _a, _b, _c;
        return (validateTakeout(location, Date.now()).isValid && ((_a = getPickupDispatchInfo(location.deliveryInfos)) === null || _a === void 0 ? void 0 : _a.contactlessDineInInfo) && ((_c = (_b = getPickupDispatchInfo(location.deliveryInfos)) === null || _b === void 0 ? void 0 : _b.contactlessDineInInfo) === null || _c === void 0 ? void 0 : _c.enabled));
    });
}
export function getFormattedAddressWithComment(address) {
    var formattedAddress = address.formatted === NO_ADDRESS || address.formatted === DEFAULT_ADDRESS
        ? DEFAULT_ADDRESS_SHORT
        : address.formatted;
    if (!address.comment ||
        !address.properties ||
        !address.properties['com.openrest']) {
        return formattedAddress;
    }
    var addressOverride = JSON.parse(address.properties['com.openrest']).addressOverride;
    if (!addressOverride) {
        return formattedAddress;
    }
    if (addressOverride === 'override_replace') {
        return address.comment;
    }
    if (addressOverride === 'override_before') {
        return address.comment + ", " + formattedAddress;
    }
    if (addressOverride === 'override_after') {
        return formattedAddress + ", " + address.comment;
    }
    return formattedAddress;
}
function isAddressMatchingDispatchInfo(restaurant, address, dispatchInfo, dispatchTime, totalOrderPrice, options) {
    if (address === void 0) { address = EMPTY_ADDRESS; }
    if (totalOrderPrice === void 0) { totalOrderPrice = undefined; }
    if (options === void 0) { options = {}; }
    options = __assign({ skipAvailabilityCheck: false, skipMinimumPriceCheck: false, skipOutOfBoundsCheck: false }, options);
    var reasons = [];
    if (!options.skipOutOfBoundsCheck &&
        !isInPolygon(dispatchInfo.area.polygon, address.latLng)) {
        reasons.push({ type: 'out-of-bounds' });
    }
    var minPrice = dispatchInfo.minOrderPrice || 0;
    if (!options.skipMinimumPriceCheck &&
        totalOrderPrice !== undefined &&
        totalOrderPrice < minPrice) {
        reasons.push({ type: 'minimum-price', minPrice: minPrice });
    }
    if (!options.skipAvailabilityCheck &&
        !isAvailableOn(dispatchInfo.availability, dispatchTime)) {
        reasons.push({ type: 'unavailable' });
    }
    if (!hasAsapOrFutureAvailability(dispatchInfo, restaurant)) {
        reasons.push({ type: 'unavailable' });
    }
    return reasons;
}
export var getAllInBounds = simpleMemoize(function (_a) {
    var restaurant = _a.restaurant, dispatchTime = _a.dispatchTime, totalOrderPrice = _a.totalOrderPrice, address = _a.address, options = _a.options;
    var deliveries = restaurant.deliveryInfos.filter(function (di) { return di.type === 'delivery' && !di.deliveryProviderInfo; });
    var validated = deliveries.map(function (deliveryDispatchInfo) { return ({
        info: deliveryDispatchInfo,
        reasons: isAddressMatchingDispatchInfo(restaurant, address, deliveryDispatchInfo, moment(dispatchTime).tz(restaurant.timezone), totalOrderPrice, options),
    }); });
    return validated.filter(isInBounds);
});
function isInBounds(validatedDeliveryDispatchInfo) {
    return !validatedDeliveryDispatchInfo.reasons
        .map(function (r) { return r.type; })
        .includes('out-of-bounds');
}
function getMostImportantReason(validatedDeliveryDispatchInfos) {
    var weights = {
        unavailable: 100,
        'minimum-price': 50,
        'out-of-bounds': 0,
        'invalid-dropoff-address': -1,
        'dropoff-address-not-serviceable': -1,
        'invalid-address': -1,
        'pinpoint-error': -1,
    };
    if (validatedDeliveryDispatchInfos.length === 0) {
        // In case no delivery areas are available at this point,
        // it means the address does not match an of the delivery areas.
        return { type: 'out-of-bounds' };
    }
    // find the delivery area with tht samllest amount of errors
    var deliveryAreaWithLeastErrors = _(validatedDeliveryDispatchInfos)
        .sortBy([function (di) { return di.reasons.length; }, function (di) { return di.info.minOrderPrice; }])
        .first();
    if (deliveryAreaWithLeastErrors) {
        // Sorting mismatch reasons by their importance, and return the first one.
        return _(deliveryAreaWithLeastErrors.reasons)
            .uniq()
            .sortBy(function (r) { return [weights[r.type] * -1, r.minPrice || 1]; })
            .first();
    }
    else {
        return undefined;
    }
}
function getPickupDispatchInfo(deliveryInfos) {
    return deliveryInfos.find(function (di) { return di.type === 'takeout'; });
}
export function validateTakeout(restaurant, dispatchTime) {
    var pickupDispatchInfo = restaurant.deliveryInfos.find(function (di) { return di.type === 'takeout'; });
    if (!pickupDispatchInfo) {
        return {
            isValid: false,
            reason: 'takeout-not-supported',
        };
    }
    if (!isAvailableOn(pickupDispatchInfo.availability, moment(dispatchTime).tz(restaurant.timezone))) {
        return {
            isValid: false,
            reason: 'unavailable',
        };
    }
    else {
        return {
            isValid: true,
        };
    }
}
export function getMinimalOrderPriceForAllAreas(deliveryInfos, shouldConsiderDeliveryPartner, timezone) {
    var deliveries = getAvailableDeliveryInfos(deliveryInfos, timezone || '').filter(function (di) {
        return di.type === 'delivery' &&
            (shouldConsiderDeliveryPartner ? true : !di.deliveryProviderInfo);
    });
    return deliveries.length < 1 ||
        deliveries.find(function (del) { return del.minOrderPrice === undefined; })
        ? undefined
        : deliveries.sort(function (delA, delB) { return delA.minOrderPrice - delB.minOrderPrice; })[0].minOrderPrice;
}
var hasAsapOrFutureAvailability = simpleMemoize(function (dispatchInfo, restaurant) {
    var isMultiLocationEnabled = isMultiLocationSite(restaurant);
    var availabilityIteratorFactory = isMultiLocationEnabled
        ? iteratorFactory.conjunct(dispatchInfo.availability, restaurant.openTimes)
        : null;
    var isAvailableNow = isMultiLocationEnabled
        ? isAvailabilityIteratorAvailable(availabilityIteratorFactory(moment().tz(restaurant.timezone)))
        : isAvailableOn(dispatchInfo.availability, moment().tz(restaurant.timezone));
    var hasSelectableFutureDate = getDateOptions({
        availability: dispatchInfo.availability,
        availabilityIteratorFactory: availabilityIteratorFactory,
        timezone: restaurant.timezone,
        delayMins: restaurant.orders.future.delayMins,
        dishPrepareTime: dispatchInfo.delayMins,
        breakOnFirst: true,
        numberOfDays: getNumberOfDaysAheadAvailableForOrders(restaurant.orders),
    });
    return ((!restaurant.orders.asap.disabled && isAvailableNow) ||
        (!restaurant.orders.future.disabled && hasSelectableFutureDate.length > 0));
});
//# sourceMappingURL=addressLogic.js.map